import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Box,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import {
  useOrdersList,
  useCancelOrderDispatch,
  useCancelAllOrdersDispatch,
} from 'hooks/redux/exchange';
import { makeStyles } from '@material-ui/styles';
import { withStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import VList from 'components/VList';
import Order from './Order';

const useStyles = makeStyles(({ palette }) => ({
  root: {
    display: 'flex',
    flex: 1,
    height: '73vh',
    flexDirection: 'column',
  },
  orders: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  popover: {
    '& .MuiPopover-paper': {
      background: palette.inactiveBtnColor,
      '& li': {
        color: palette.secondaryColor,
      },
    },
  },
  orderTypeText: {
    color: palette.secondaryColor,
    fontSize: '16px',
    fontWeight: '400',
    margin: '1rem 0 0.5rem 0',
  },
  expandIcon: {
    color: '#74ebed',
    fontSize: '30px',
    marginLeft: '1px',
  },
  cancelAll: {
    font: '16px SofiaPro-Medium',
    fontWeight: '450',
    margin: '1rem 0 0.5rem 0',
    color: '#00DDE1',
  },
  ordersList: {
    margin: '20px 0 10px 0',
    padding: '0 10px',
    boxShadow: '0 3px 10px 1px rgba(0,0,0,0.15)',
    borderRadius: '6px',
  },
  vListBox: {
    margin: '10px 0 10px',
    height: '100%',
  },
}));

const StyledMenu = withStyles({
  paper: {
    border: '1px solid #d3d4d5',
  },
})((props) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center',
    }}
    {...props}
  />
));

const StyledMenuItem = withStyles(({ palette }) => ({
  root: {
    color: 'black',
    '&:focus': {
      color: 'black',
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: palette.common.white,
      },
    },
  },
}))(MenuItem);

const OpenOrders = ({ market }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showOrder, setShowOrder] = useState('all');
  const [ordersList, setOrdersList] = useState([]);
  const [orgOrders, setOrgOrders] = useState(null);
  const [params, setParams] = useState();
  const classes = useStyles();

  const orders = useOrdersList(params);
  const cancelOrder = useCancelOrderDispatch();
  const cancelAllOrder = useCancelAllOrdersDispatch();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleScroll = (event) => {
    if (params) {
      const { page } = params;
      if (((event.scrollHeight - event.clientHeight) === Math.round(event.scrollTop)) && ordersList.length >= (page + 1) * params.amount) {
        setParams({
          ...params,
          page: page + 1,
        });
      }
    }
  };

  const cancelOpenOrder = async (order) => {
    await cancelOrder({
      id: order.id,
    });
    const remainningOrders = ordersList.filter((selectedOrder) => selectedOrder.id !== order.id);
    setOrdersList(remainningOrders);
  };

  const cancelAllOrders = async () => {
    let cparams = {};
    if (showOrder === 'all') {
      cparams = {
        market_id: market.id,
        open_closed: 'open',
      };
    } else if (showOrder === 'buy') {
      cparams = {
        market_id: market.id,
        open_closed: 'open',
        is_buying: true,
      };
    } else if (showOrder === 'sell') {
      cparams = {
        market_id: market.id,
        open_closed: 'open',
        is_buying: false,
      };
    }
    await cancelAllOrder(cparams);
  };

  useEffect(() => {
    if (showOrder) {
      if (showOrder === 'buy') {
        setParams({
          market_id: market.id,
          page: 0,
          amount: 25,
          open_closed: 'open',
          is_buying: true,
        });
      } else if (showOrder === 'sell') {
        setParams({
          market_id: market.id,
          page: 0,
          amount: 25,
          open_closed: 'open',
          is_buying: false,
        });
      } else {
        setParams({
          market_id: market.id,
          page: 0,
          amount: 25,
          open_closed: 'open',
        });
      }
    }
    // eslint-disable-next-line
  }, [showOrder]);

  useEffect(() => {
    if (orgOrders === orders) {
      return;
    }
    if (params && params.page === 0) {
      setOrdersList([
        ...(orders || []),
      ]);
    } else {
      setOrdersList([
        ...ordersList,
        ...(orders || []),
      ]);
    }
    setOrgOrders(orders);
  }, [orders, orgOrders, ordersList, params]);

  return (
    <>
      <Box className={classes.root}>
        <StyledMenu
          id="customized-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          className={classes.popover}
          onClose={handleClose}
        >
          <StyledMenuItem onClick={() => { setShowOrder('all'); handleClose(); }}>
            All Orders
          </StyledMenuItem>
          <StyledMenuItem onClick={() => { setShowOrder('buy'); handleClose(); }}>
            Limit Buy Orders
          </StyledMenuItem>
          <StyledMenuItem onClick={() => { setShowOrder('sell'); handleClose(); }}>
            Limit Sell Orders
          </StyledMenuItem>
        </StyledMenu>
        <Box className={classes.orders}>
          <Box
            display="flex"
            alignItems="flex-end"
            aria-controls="customized-menu"
            aria-haspopup="true"
            onClick={handleClick}
          >
            <Typography className={classes.orderTypeText} variant="h1">
              {
                showOrder === 'all' ? 'All Orders' : showOrder === 'buy' ? 'Limit Buy Orders' : 'Limit Sell Orders'
              }
            </Typography>
            <ExpandMoreIcon
              className={classes.expandIcon}
            />
          </Box>
          <Typography
            className={classes.cancelAll}
            onClick={() => cancelAllOrders()}
          >
            Cancel All
          </Typography>
        </Box>
        <Box className={classes.vListBox}>
          {ordersList.length > 0 && (
            <VList
              onScroll={handleScroll}
              rowCount={ordersList.length}
              rowRender={(index) => (
                <Order
                  item={ordersList[index]}
                  onCancel={() => cancelOpenOrder(ordersList[index])}
                />
              )}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

OpenOrders.propTypes = {
  market: PropTypes.object.isRequired,
};
const mapStateToProps = () => ({});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(OpenOrders);
